// pages/quotations/AddQuotationPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import {
    Trash2,
    Plus,
    ArrowLeft,
    Save,
    X,
    RotateCcw,
    Search,

} from 'react-feather';
import Select from 'react-select';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { quotationActions } from '../../core/action/quotationActions';
import { customerActions } from '../../core/action/customerActions';
import { productActions } from '../../core/action/productActions';
import AddCustomer from '../../core/modals/peoples/AddCustomer';

const AddQuotationPage = () => {
    const navigate = useNavigate();
    const searchInputRef = useRef(null);
    const productSearchTimeout = useRef(null);

    // Generate unique quotation number
    const generateQuotationNo = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `QTN${year}${month}${day}${random}`;
    };

    // Rounding function
    const roundAmount = (amount) => {
        return Math.round(amount);
    };

    // Initial state
    const [formData, setFormData] = useState({
        quotation_no: generateQuotationNo(),
        reference_no: `REF-${Date.now()}`,
        quotation_date: dayjs().format('YYYY-MM-DD'),
        expiry_date: dayjs().add(15, 'day').format('YYYY-MM-DD'), // Valid for 15 days
        customer_id: '',
        customer_name: '',
        customer_phone: '',
        no_of_items: 0,
        total_qty: 0,
        subtotal: 0,
        tax: 0,
        discount: 0,
        shipping: 0,
        adjustment: 0,
        grand_total: 0,
        rounded_grand_total: 0,
        round_off: 0,
        enable_rounding: true,
        notes: '',
        terms_conditions: '1. This is a quotation and not an invoice.\n2. Prices are valid for 15 days.\n3. Taxes as applicable.',
        status: 'draft',
        items: []
    });

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedExpiryDate, setSelectedExpiryDate] = useState(dayjs().add(15, 'day'));
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerError, setCustomerError] = useState('');

    // Cache for product searches
    const productCache = useRef(new Map());

    // Status options
    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' }
    ];

    // Load customers
    const loadCustomers = useCallback(async (search = '') => {
        try {
            setLoadingCustomers(true);
            setCustomerError('');

            const params = { limit: 50 };
            if (search) {
                params.search = search;
            }

            const result = await customerActions.getCustomers(params);

            if (result.success) {
                if (result.data && result.data.length > 0) {
                    const customerList = result.data.map(c => ({
                        value: c.id,
                        label: `${c.name} ${c.phone ? `(${c.phone})` : ''}`,
                        phone: c.phone,
                        email: c.email,
                        address: c.address,
                        ...c
                    }));
                    setCustomers(customerList);
                } else {
                    setCustomers([]);
                }
            } else {
                setCustomerError('Failed to load customers');
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setCustomerError('Error loading customers');
        } finally {
            setLoadingCustomers(false);
        }
    }, []);

    // Load initial customers
    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    // Handle customer search
    const handleCustomerSearch = (inputValue) => {
        if (inputValue.length >= 2) {
            loadCustomers(inputValue);
        }
        return inputValue;
    };

    // Optimized product search with caching
    const searchProducts = async (term) => {
        if (term.length < 2) {
            setSearchResults([]);
            setShowProductSearch(false);
            setSearching(false);
            return;
        }

        if (productCache.current.has(term)) {
            setSearchResults(productCache.current.get(term));
            setShowProductSearch(true);
            setSearching(false);
            return;
        }

        try {
            setSearching(true);
            const result = await productActions.searchProducts(term);

            if (result.success) {
                const limitedResults = (result.data || []).slice(0, 20);
                productCache.current.set(term, limitedResults);
                setSearchResults(limitedResults);
                setShowProductSearch(true);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Search products with debounce
    const handleProductSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);

        if (productSearchTimeout.current) {
            clearTimeout(productSearchTimeout.current);
        }

        productSearchTimeout.current = setTimeout(() => {
            searchProducts(term);
        }, 300);
    };

    // Calculate all totals
    const calculateTotals = useCallback((items, discount = 0, shipping = 0, adjustment = 0, enableRounding = true) => {
        const no_of_items = items.length;
        const total_qty = items.reduce((sum, i) => sum + (parseFloat(i.quantity || 0)), 0);

        const subtotal = items.reduce((sum, i) => sum + (parseFloat(i.unit_price || 0) * parseFloat(i.quantity || 0)), 0);

        const totalTax = items.reduce((sum, i) => sum + parseFloat(i.tax_amount || 0), 0);

        const grandTotal = subtotal - parseFloat(discount) + totalTax + parseFloat(shipping) + parseFloat(adjustment);

        let roundedGrandTotal = grandTotal;
        let roundOff = 0;

        if (enableRounding) {
            roundedGrandTotal = roundAmount(grandTotal);
            roundOff = roundedGrandTotal - grandTotal;
        }

        return {
            no_of_items,
            total_qty,
            subtotal: parseFloat(subtotal.toFixed(2)),
            tax: parseFloat(totalTax.toFixed(2)),
            grand_total: parseFloat(grandTotal.toFixed(2)),
            rounded_grand_total: parseFloat(roundedGrandTotal.toFixed(2)),
            round_off: parseFloat(roundOff.toFixed(2))
        };
    }, []);

    // Toggle rounding
    const handleToggleRounding = () => {
        setFormData(prev => {
            const newEnableRounding = !prev.enable_rounding;
            const totals = calculateTotals(
                prev.items,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                newEnableRounding
            );

            return {
                ...prev,
                enable_rounding: newEnableRounding,
                ...totals
            };
        });
    };

    // Add item to quotation
    const handleAddItem = (product) => {
        const existingItem = formData.items.find(i => i.product_id === product.id);

        if (existingItem) {
            const index = formData.items.indexOf(existingItem);
            handleQuantityChange(index, existingItem.quantity + 1);
            return;
        }

        const unitPrice = parseFloat(product.selling_price || 0);
        const taxPercent = parseFloat(product.tax_rate || 0);
        const sgstPercent = taxPercent / 2;
        const cgstPercent = taxPercent / 2;

        const subtotal = unitPrice * 1;
        const discountAmount = 0;
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = (taxableAmount * taxPercent) / 100;
        const sgstAmount = taxAmount / 2;
        const cgstAmount = taxAmount / 2;
        const totalCost = taxableAmount + taxAmount;

        const newItem = {
            id: `temp_${Date.now()}_${Math.random()}`,
            product_id: product.id,
            product_code: product.sku,
            product_name: product.product_name,
            sku: product.sku,
            units: 'PCS',
            quantity: 1,
            unit_price: unitPrice,
            discount: 0,
            discount_amount: 0,
            tax_percent: taxPercent,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent,
            sgst_amount: sgstAmount,
            cgst_amount: cgstAmount,
            igst_amount: 0,
            unit_cost: parseFloat(product.cost_price || 0),
            total_cost: totalCost,
            category_id: product.category_id,
            brand_id: product.brand_id
        };

        setFormData(prev => {
            const updatedItems = [...prev.items, newItem];
            const totals = calculateTotals(
                updatedItems,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });

        setSearchTerm('');
        setShowProductSearch(false);
    };

    // Handle quantity change
    const handleQuantityChange = (index, value) => {
        const qty = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const actualQty = Math.max(0, qty);

            const subtotal = item.unit_price * actualQty;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                quantity: actualQty,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle price change
    const handlePriceChange = (index, value) => {
        const price = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = price * item.quantity;
            const discountAmount = (subtotal * (item.discount || 0)) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                unit_price: price,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle discount change
    const handleDiscountChange = (index, value) => {
        const discountPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const subtotal = item.unit_price * item.quantity;
            const discountAmount = (subtotal * discountPercent) / 100;
            const taxableAmount = subtotal - discountAmount;
            const taxAmount = (taxableAmount * (item.tax_percent || 0)) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                discount: discountPercent,
                discount_amount: discountAmount,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Handle tax change
    const handleTaxChange = (index, value) => {
        const taxPercent = parseFloat(value) || 0;

        setFormData(prev => {
            const updatedItems = [...prev.items];
            const item = { ...updatedItems[index] };

            const taxableAmount = item.unit_price * item.quantity - (item.discount_amount || 0);
            const taxAmount = (taxableAmount * taxPercent) / 100;
            const sgstAmount = taxAmount / 2;
            const cgstAmount = taxAmount / 2;
            const totalCost = taxableAmount + taxAmount;

            updatedItems[index] = {
                ...item,
                tax_percent: taxPercent,
                sgst_percent: taxPercent / 2,
                cgst_percent: taxPercent / 2,
                sgst_amount: sgstAmount,
                cgst_amount: cgstAmount,
                tax_amount: taxAmount,
                total_cost: totalCost
            };

            const totals = calculateTotals(
                updatedItems,
                prev.discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );

            return {
                ...prev,
                items: updatedItems,
                ...totals
            };
        });
    };

    // Remove item
    const handleRemoveItem = (index) => {
        Swal.fire({
            title: 'Remove Item?',
            text: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData(prev => {
                    const updatedItems = prev.items.filter((_, i) => i !== index);
                    const totals = calculateTotals(
                        updatedItems,
                        prev.discount,
                        prev.shipping,
                        prev.adjustment,
                        prev.enable_rounding
                    );

                    return {
                        ...prev,
                        items: updatedItems,
                        ...totals
                    };
                });
            }
        });
    };

    // Handle customer change
    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setFormData(prev => ({
            ...prev,
            customer_id: option?.value || '',
            customer_name: option?.label || '',
            customer_phone: option?.phone || ''
        }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
            setFormData(prev => ({ ...prev, quotation_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle expiry date change
    const handleExpiryDateChange = (date) => {
        if (date) {
            setSelectedExpiryDate(date);
            setFormData(prev => ({ ...prev, expiry_date: date.format('YYYY-MM-DD') }));
        }
    };

    // Handle status change
    const handleStatusChange = (option) => {
        setFormData(prev => ({ ...prev, status: option?.value || 'draft' }));
    };

    // Handle discount amount change
    const handleDiscountAmountChange = (value) => {
        const discount = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                discount,
                prev.shipping,
                prev.adjustment,
                prev.enable_rounding
            );
            return {
                ...prev,
                discount,
                ...totals
            };
        });
    };

    // Handle shipping change
    const handleShippingChange = (value) => {
        const shipping = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.discount,
                shipping,
                prev.adjustment,
                prev.enable_rounding
            );
            return {
                ...prev,
                shipping,
                ...totals
            };
        });
    };

    // Handle adjustment change
    const handleAdjustmentChange = (value) => {
        const adjustment = parseFloat(value) || 0;
        setFormData(prev => {
            const totals = calculateTotals(
                prev.items,
                prev.discount,
                prev.shipping,
                adjustment,
                prev.enable_rounding
            );
            return {
                ...prev,
                adjustment,
                ...totals
            };
        });
    };

    // Handle customer added from modal
    const handleCustomerAdded = (newCustomer) => {
        let customerData = newCustomer;

        if (newCustomer.data) {
            customerData = newCustomer.data;
        }

        if (newCustomer.customer) {
            customerData = newCustomer.customer;
        }

        if (!customerData || !customerData.id) {
            console.error('Invalid customer data received:', newCustomer);
            Swal.fire('Error', 'Invalid customer data received', 'error');
            return;
        }

        loadCustomers();

        const customerOption = {
            value: customerData.id,
            label: `${customerData.name} ${customerData.phone ? `(${customerData.phone})` : ''}`,
            phone: customerData.phone,
            email: customerData.email,
            address: customerData.address
        };

        setSelectedCustomer(customerOption);
        setFormData(prev => ({
            ...prev,
            customer_id: customerData.id,
            customer_name: customerOption.label,
            customer_phone: customerData.phone || ''
        }));

        Swal.fire({
            icon: 'success',
            title: 'Customer Added',
            text: `${customerData.name} has been added successfully`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    // Validate form
    const validateForm = () => {
        if (!formData.customer_id) {
            Swal.fire('Error', 'Please select a customer', 'error');
            return false;
        }

        if (formData.items.length === 0) {
            Swal.fire('Error', 'Please add at least one item', 'error');
            return false;
        }

        return true;
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setLoading(true);

            const itemsToSend = formData.items.map(({ ...item }) => item);

            const finalGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

            const dataToSend = {
                ...formData,
                items: itemsToSend,
                grand_total: finalGrandTotal,
                round_off: formData.round_off
            };

            console.log('Sending quotation data:', JSON.stringify(dataToSend, null, 2));

            const result = await quotationActions.createQuotation(dataToSend);

            if (result.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Quotation created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate('/quotation-list');
            } else {
                console.error('Error response:', result);
                Swal.fire('Error', result.message || 'Failed to create quotation', 'error');
            }
        } catch (error) {
            console.error('Error creating quotation:', error);
            Swal.fire('Error', 'Failed to create quotation', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (formData.items.length > 0 || formData.customer_id) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/quotation-list');
                }
            });
        } else {
            navigate('/quotation-list');
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toFixed(2)}`;
    };

    // Cleanup
    useEffect(() => {
        return () => {
            if (productSearchTimeout.current) {
                clearTimeout(productSearchTimeout.current);
            }
        };
    }, []);

    // Get current grand total
    const currentGrandTotal = formData.enable_rounding ? formData.rounded_grand_total : formData.grand_total;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Create Quotation</h4>
                        <h6>Create new sales quotation for customer</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleCancel}
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Quotations
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Form */}
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            {/* Quotation Number and Date Row */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Quotation No</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.quotation_no}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Reference No</label>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={formData.reference_no}
                                        readOnly
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Quotation Date</label>
                                    <DatePicker
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                        allowClear={false}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Valid Till</label>
                                    <DatePicker
                                        value={selectedExpiryDate}
                                        onChange={handleExpiryDateChange}
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        style={{ width: '100%' }}
                                        allowClear={false}
                                    />
                                </div>
                            </div>

                            {/* Customer Row */}
                            <div className="row mb-3">
                                <div className="col-md-5">
                                    <label className="form-label fw-bold">Customer Name</label>
                                    <div className="d-flex">
                                        <div style={{ flex: 1 }}>
                                            {loadingCustomers ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                                                    <span>Loading customers...</span>
                                                </div>
                                            ) : (
                                                <Select
                                                    options={customers}
                                                    value={selectedCustomer}
                                                    onChange={handleCustomerChange}
                                                    onInputChange={handleCustomerSearch}
                                                    placeholder="Choose customer"
                                                    isClearable
                                                    isLoading={loadingCustomers}
                                                />
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary ms-2"
                                            onClick={() => setShowCustomerModal(true)}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    {customerError && (
                                        <small className="text-danger">{customerError}</small>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label fw-bold">Phone</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.customer_phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold">Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={statusOptions.find(opt => opt.value === formData.status)}
                                        onChange={handleStatusChange}
                                        placeholder="Select Status"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold">Quick Search</label>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary w-100"
                                        onClick={() => searchInputRef.current?.focus()}
                                    >
                                        <Search size={16} className="me-2" />
                                        Search Product
                                    </button>
                                </div>
                            </div>

                            {/* Product Search */}
                            <div className="row mb-3">
                                <div className="col-12">
                                    <label className="form-label fw-bold">Product Name / Code</label>
                                    <div className="position-relative">
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="form-control"
                                            placeholder="Type product name or code..."
                                            value={searchTerm}
                                            onChange={handleProductSearch}
                                        />

                                        {searching && (
                                            <div className="position-absolute end-0 top-0 mt-2 me-3">
                                                <div className="spinner-border spinner-border-sm text-primary" />
                                            </div>
                                        )}

                                        {showProductSearch && searchResults.length > 0 && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 9999, maxHeight: '300px', overflowY: 'auto' }}>
                                                {searchResults.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className="p-2 border-bottom"
                                                        onClick={() => handleAddItem(product)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="d-flex justify-content-between">
                                                            <div>
                                                                <strong>{product.product_name}</strong>
                                                                <br />
                                                                <small className="text-muted">Code: {product.sku}</small>
                                                            </div>
                                                            <div className="text-end">
                                                                <span className="text-primary">{formatCurrency(product.selling_price)}</span>
                                                                <br />
                                                                <small className="text-muted">Stock: {product.quantity || 0}</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Items Table */}
                            {formData.items.length > 0 && (
                                <div className="table-responsive mb-4">
                                    <table className="table table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product Code</th>
                                                <th>Product Name</th>
                                                <th>Units</th>
                                                <th className="text-end">Qty</th>
                                                <th className="text-end">Rate</th>
                                                <th className="text-end">Disc %</th>
                                                <th className="text-end">GST %</th>
                                                <th className="text-end">Amount</th>
                                                <th className="text-center">Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={item.product_code || ''}
                                                            readOnly
                                                        />
                                                    </td>
                                                    <td>
                                                        <strong>{item.product_name}</strong>
                                                    </td>
                                                    <td>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={item.units}
                                                            onChange={(e) => {
                                                                const updated = [...formData.items];
                                                                updated[index].units = e.target.value;
                                                                setFormData(prev => ({ ...prev, items: updated }));
                                                            }}
                                                        >
                                                            <option value="PCS">PCS</option>
                                                            <option value="BOX">BOX</option>
                                                            <option value="KG">KG</option>
                                                            <option value="LTR">LTR</option>
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={item.quantity}
                                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                            min="0"
                                                            step="0.1"
                                                            style={{ width: '80px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={item.unit_price}
                                                            onChange={(e) => handlePriceChange(index, e.target.value)}
                                                            min="0"
                                                            step="0.01"
                                                            style={{ width: '100px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={item.discount || 0}
                                                            onChange={(e) => handleDiscountChange(index, e.target.value)}
                                                            min="0"
                                                            step="0.1"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end"
                                                            value={item.tax_percent || 0}
                                                            onChange={(e) => handleTaxChange(index, e.target.value)}
                                                            min="0"
                                                            step="0.1"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-bold">
                                                        {formatCurrency(item.total_cost)}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => handleRemoveItem(index)}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Summary Section */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">No of Items</span>
                                                <span className="fw-bold text-primary">{formData.no_of_items}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Total Qty</span>
                                                <span className="fw-bold text-info">{formData.total_qty}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Subtotal</span>
                                                <span className="fw-bold text-success">{formatCurrency(formData.subtotal)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                                                <span className="fw-semibold">Tax</span>
                                                <span className="fw-bold text-warning">{formatCurrency(formData.tax)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="bg-primary text-white p-3 rounded">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5 className="mb-0 text-white">Total Amount</h5>
                                            <div className="d-flex align-items-center">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm ${formData.enable_rounding ? 'btn-light' : 'btn-outline-light'} me-2`}
                                                    onClick={handleToggleRounding}
                                                    title={formData.enable_rounding ? "Rounding Enabled" : "Rounding Disabled"}
                                                >
                                                    <RotateCcw size={14} className="me-1" />
                                                    {formData.enable_rounding ? 'Rounded' : 'Actual'}
                                                </button>
                                                <h3 className="mb-0 text-white">
                                                    {formatCurrency(currentGrandTotal)}
                                                </h3>
                                            </div>
                                        </div>
                                        {formData.enable_rounding && formData.round_off !== 0 && (
                                            <div className="d-flex justify-content-end">
                                                <small className="text-white-50">
                                                    (Actual: {formatCurrency(formData.grand_total)} |
                                                    Round Off: {formData.round_off > 0 ? '+' : ''}{formatCurrency(formData.round_off)})
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Discount, Shipping, Adjustment Row */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Additional Discount ₹ </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.discount}
                                        onChange={(e) => handleDiscountAmountChange(e.target.value)}
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Shipping</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.shipping}
                                        onChange={(e) => handleShippingChange(e.target.value)}
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Adjustment (+/-)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={formData.adjustment}
                                        onChange={(e) => handleAdjustmentChange(e.target.value)}
                                        step="0.1"
                                    />
                                </div>
                            </div>

                            {/* Notes and Terms */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Notes</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={formData.notes}
                                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Additional notes for customer"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Terms & Conditions</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={formData.terms_conditions}
                                        onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                                        placeholder="Terms and conditions"
                                    />
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={16} className="me-2" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || formData.items.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            Create Quotation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Customer Modal */}
            {showCustomerModal && (
                <AddCustomer
                    isOpen={showCustomerModal}
                    onClose={() => setShowCustomerModal(false)}
                    onSuccess={handleCustomerAdded}
                />
            )}
        </div>
    );
};

export default AddQuotationPage;